import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FaArrowLeft, FaCheck, FaTimes, FaRedo } from "react-icons/fa";
import Loader from "../../../../components/loader";
import api from "../../../../services/api";
import { TRAINING_TEST_QUESTIONS, calculateEvaluationScoresFromTest } from "./test-questions";

export default function Test() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const userRes = await api.get(`/user/${id}`);
      if (!userRes.ok) {
        toast.error("User not found");
        navigate("/selecole/tracker");
        return;
      }
      setUser(userRes.data);
      
      // Load existing test answers if any
      if (userRes.data.training_test_answers) {
        setAnswers(userRes.data.training_test_answers);
      }
      
      // Check if test already submitted - if so, show results only, no retaking
      if (userRes.data.training_test_completed_at) {
        setSubmitted(true);
        if (userRes.data.training_test_results) {
          setTestResults(userRes.data.training_test_results);
        }
        // Load submitted answers to show what they answered
        if (userRes.data.training_test_answers) {
          setAnswers(userRes.data.training_test_answers);
        }
      }
    } catch (error) {
      console.log(error);
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answerIndex) => {
    if (submitted) {
      toast.error("Test already submitted. You cannot change answers.");
      return;
    }
    setAnswers({ ...answers, [questionId]: answerIndex });
  };

  const saveProgress = async () => {
    try {
      await api.put(`/user/${id}`, {
        training_test_answers: answers,
      });
    } catch (error) {
      console.log("Error saving progress:", error);
    }
  };

  useEffect(() => {
    // Auto-save progress every 30 seconds
    if (!submitted && Object.keys(answers).length > 0) {
      const timer = setTimeout(saveProgress, 30000);
      return () => clearTimeout(timer);
    }
  }, [answers, submitted]);

  const clearAnswers = async () => {
    if (!confirm("Clear all answers and start over? This will reset your progress.")) {
      return;
    }

    try {
      const { ok } = await api.put(`/user/${id}`, {
        training_test_answers: {},
        training_test_results: {},
        training_test_completed_at: null,
        training_test_evaluation_scores: {},
      });

      if (!ok) return toast.error("Failed to clear answers");
      
      setAnswers({});
      setSubmitted(false);
      setTestResults(null);
      setCurrentQuestionIndex(0);
      toast.success("Answers cleared! You can start fresh.");
    } catch (error) {
      console.log(error);
      toast.error("Error clearing answers");
    }
  };

  const submitTest = async () => {
    if (!confirm("Are you sure you want to submit? You won't be able to change your answers.")) {
      return;
    }

    try {
      // Calculate results
      const results = {
        totalQuestions: TRAINING_TEST_QUESTIONS.length,
        answered: Object.keys(answers).length,
        correct: 0,
        incorrect: 0,
        unanswered: 0,
        byCriterion: {},
        byPillar: { 
          deliverables: { correct: 0, total: 0 },
          skills: { correct: 0, total: 0 },
          culture: { correct: 0, total: 0 }
        },
      };

      TRAINING_TEST_QUESTIONS.forEach((q) => {
        const answer = answers[q.id];
        
        // Track total questions per pillar
        results.byPillar[q.pillar].total++;
        
        if (answer === undefined) {
          results.unanswered++;
          return;
        }

        if (answer === q.correctAnswer) {
          results.correct++;
          results.byPillar[q.pillar].correct++;
        } else {
          results.incorrect++;
        }

        // Track by criterion
        if (!results.byCriterion[q.criterion]) {
          results.byCriterion[q.criterion] = { correct: 0, total: 0 };
        }
        results.byCriterion[q.criterion].total++;
        if (answer === q.correctAnswer) {
          results.byCriterion[q.criterion].correct++;
        }
      });

      // Calculate evaluation scores from test
      const evaluationScores = calculateEvaluationScoresFromTest(answers);

      const { ok } = await api.put(`/user/${id}`, {
        training_test_answers: answers,
        training_test_results: results,
        training_test_completed_at: new Date().toISOString(),
        training_test_evaluation_scores: evaluationScores, // Auto-populate evaluation scores
      });

      if (!ok) return toast.error("Failed to submit test");
      
      setSubmitted(true);
      setTestResults(results);
      toast.success("Test submitted successfully!");
    } catch (error) {
      console.log(error);
      toast.error("Error submitting test");
    }
  };

  if (loading) return <Loader />;
  if (!user) return <div className="p-8 text-center text-gray-500">User not found</div>;

  const currentQuestion = TRAINING_TEST_QUESTIONS[currentQuestionIndex];
  const totalQuestions = TRAINING_TEST_QUESTIONS.length;
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / totalQuestions) * 100;

  // Group questions by pillar for navigation
  const questionsByPillar = {
    deliverables: TRAINING_TEST_QUESTIONS.filter(q => q.pillar === "deliverables"),
    skills: TRAINING_TEST_QUESTIONS.filter(q => q.pillar === "skills"),
    culture: TRAINING_TEST_QUESTIONS.filter(q => q.pillar === "culture"),
  };

  if (submitted && testResults) {
    // Show results
    return (
      <div className="p-6 space-y-6 bg-gray-50 min-h-full">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate("/selecole/tracker")}
              className="p-2 hover:bg-white rounded-lg transition-colors"
            >
              <FaArrowLeft className="text-gray-500" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Test Results</h1>
          </div>
          <button
            onClick={clearAnswers}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
          >
            <FaRedo /> Start Over
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{testResults.correct}</div>
              <div className="text-sm text-gray-500">Correct</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">{testResults.incorrect}</div>
              <div className="text-sm text-gray-500">Incorrect</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-600">{testResults.unanswered}</div>
              <div className="text-sm text-gray-500">Unanswered</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">
                {Math.round((testResults.correct / testResults.totalQuestions) * 100)}%
              </div>
              <div className="text-sm text-gray-500">Score</div>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="font-semibold text-gray-900">Performance by Pillar</h2>
            {Object.entries(questionsByPillar).map(([pillar, questions]) => {
              const pillarCorrect = questions.filter(q => answers[q.id] === q.correctAnswer).length;
              const pillarTotal = questions.length;
              const pillarPercentage = pillarTotal > 0 ? Math.round((pillarCorrect / pillarTotal) * 100) : 0;
              
              return (
                <div key={pillar} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700 capitalize">{pillar}</span>
                    <span className="text-sm text-gray-600">{pillarCorrect}/{pillarTotal} correct ({pillarPercentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${pillarPercentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Your test results have been saved and will help inform your training evaluation. 
              Your trainer will review these results along with your practical work.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate("/selecole/tracker")}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <FaArrowLeft className="text-gray-500" />
          </button>
          
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Training Test</h1>
            <p className="text-sm text-gray-500">Question {currentQuestionIndex + 1} of {totalQuestions}</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            {answeredCount}/{totalQuestions} answered
          </div>
          {Object.keys(answers).length > 0 && (
            <button
              onClick={clearAnswers}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
            >
              <FaRedo /> Clear Answers
            </button>
          )}
          <button
            onClick={submitTest}
            disabled={answeredCount === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Submit Test
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm text-gray-600">{Math.round(progress)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <span className="px-3 py-1 text-xs font-semibold text-gray-600 bg-gray-100 rounded-full uppercase">
            {currentQuestion.pillar}
          </span>
          <span className="px-3 py-1 text-xs font-medium text-gray-500 bg-gray-50 rounded-full">
            {currentQuestion.criterion}
          </span>
          <span className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-full">
            {currentQuestion.type}
          </span>
        </div>
        
        <h2 className="text-xl font-semibold text-gray-900 mb-6 leading-relaxed">
          {currentQuestion.question}
        </h2>

        {currentQuestion.type === "code_review" && currentQuestion.question.includes("```") && (
          <div className="mb-6 p-4 bg-gray-900 text-gray-100 rounded-lg font-mono text-sm overflow-x-auto">
            <pre>{currentQuestion.question.match(/```[\s\S]*?```/)?.[0]?.replace(/```/g, '')}</pre>
          </div>
        )}

        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => {
            const isSelected = answers[currentQuestion.id] === index;
            const isCorrect = index === currentQuestion.correctAnswer;
            
            return (
              <button
                key={index}
                onClick={() => handleAnswer(currentQuestion.id, index)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  isSelected
                    ? "border-blue-600 bg-blue-50 shadow-sm"
                    : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/30"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    isSelected ? "border-blue-600 bg-blue-600" : "border-gray-300 bg-white"
                  }`}>
                    {isSelected && <FaCheck className="text-white text-xs" />}
                  </div>
                  <span className="flex-1 text-gray-700 leading-relaxed">{option}</span>
                </div>
              </button>
            );
          })}
        </div>

        {submitted && currentQuestion.explanation && answers[currentQuestion.id] !== undefined && (
          <div className={`mt-4 p-4 rounded-lg ${
            answers[currentQuestion.id] === currentQuestion.correctAnswer
              ? "bg-green-50 border border-green-200"
              : "bg-yellow-50 border border-yellow-200"
          }`}>
            <div className="flex items-start gap-2">
              {answers[currentQuestion.id] === currentQuestion.correctAnswer ? (
                <FaCheck className="text-green-600 mt-0.5" />
              ) : (
                <FaTimes className="text-yellow-600 mt-0.5" />
              )}
              <div>
                <div className="font-medium text-sm mb-1">
                  {answers[currentQuestion.id] === currentQuestion.correctAnswer ? "Correct!" : "Not quite"}
                </div>
                <div className="text-sm text-gray-700">{currentQuestion.explanation}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Question Navigation</h3>
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 bg-gray-50 rounded-lg">
            {TRAINING_TEST_QUESTIONS.map((q, idx) => {
              const isAnswered = answers[q.id] !== undefined;
              const isCurrent = idx === currentQuestionIndex;
              
              return (
                <button
                  key={q.id}
                  onClick={() => setCurrentQuestionIndex(idx)}
                  className={`min-w-[2.5rem] h-10 rounded-lg text-sm font-medium transition-all ${
                    isCurrent
                      ? "bg-blue-600 text-white shadow-md scale-105"
                      : isAnswered
                      ? "bg-green-100 text-green-700 hover:bg-green-200 border-2 border-green-300"
                      : "bg-white text-gray-600 hover:bg-gray-100 border-2 border-gray-300"
                  }`}
                  title={`Question ${idx + 1}: ${q.criterion}`}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
            disabled={currentQuestionIndex === 0}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            ← Previous
          </button>

          <div className="text-sm text-gray-600">
            Question <span className="font-semibold">{currentQuestionIndex + 1}</span> of <span className="font-semibold">{totalQuestions}</span>
          </div>

          <button
            onClick={() => setCurrentQuestionIndex(Math.min(totalQuestions - 1, currentQuestionIndex + 1))}
            disabled={currentQuestionIndex === totalQuestions - 1}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
